import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Anestesi = {
    id: number;
    code: string;
    displayName: string;
    is_blockad: boolean;
    ordning: number;
    huvudanestesi_id?: number;
};

export type AnestesiDisplay = {
    value: Anestesi["id"];
    label: string;
    is_blockad: boolean;
};

type State = BaseState & {
    anestesi: Map<Anestesi["id"], Anestesi>;
};

export const convertAnestesiData: (args: { anestesi?: Anestesi }) => AnestesiDisplay | null = ({ anestesi }) => {
    if (!anestesi) {
        return null;
    }

    return {
        value: anestesi.id,
        label: anestesi.displayName,
        is_blockad: anestesi.is_blockad,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        anestesi: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "anestesi";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        anestesi: (state: State) => [...state.anestesi.values()],
        anestesiSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertAnestesiData({ anestesi: singleResult }));
            }

            return result;
        },
        anestesiById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.anestesi.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addAnestesi: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                anestesi = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                anestesi: Anestesi[];
            }
        ) =>
            subsetSetter<State, Anestesi>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: anestesi,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchAnestesi: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Anestesi>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "operation/kodverk/anestesier/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addAnestesi", {
                    subsetName,
                    offset: resultOffset,
                    anestesi: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertAnestesiData({ anestesi: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
